import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/midnight-lab.css';

const robotData = {
    '2025-26': {
      name: 'Decode',
      description: 'Built for speed and consistency, featuring advanced mecanum drive and precision scoring mechanisms.',
      image: '/assets/IMG_3291.jpeg',
      specs: {
        driveType: 'Mecanum',
        weight: '18 lbs',
        cycleTime: '8.5s',
        maxSpeed: '3.2 ft/s',
        autoConsistency: '92%',
        keyMechanism: 'Dual Intake + Linear Lift',
        controlSystem: 'REV Hub + Expansion Hub',
        awards: 'State Qualifier, Design Award'
      },
      events: [
        { name: 'Virginia State Championship', placement: '12th', date: '2025-03-15' },
        { name: 'Regional Qualifier #1', placement: '3rd', date: '2025-02-10' },
        { name: 'Regional Qualifier #2', placement: '5th', date: '2025-02-24' }
      ],
      buildSteps: [
        { name: 'Chassis', description: 'Aluminum frame with reinforced corners', parts: 'Aluminum extrusions, corner brackets' },
        { name: 'Drivetrain', description: 'Mecanum wheels with 4 REV HD Hex motors', parts: 'REV HD Hex motors, mecanum wheels, gearboxes' },
        { name: 'Intake', description: 'Dual roller intake system', parts: 'REV Core Hex motors, custom rollers' },
        { name: 'Lift', description: 'Linear slide lift mechanism', parts: 'Linear slides, REV Core Hex motor, spool' },
        { name: 'Outtake', description: 'Precision scoring mechanism', parts: 'Servos, custom scoring arm' },
        { name: 'Wiring', description: 'Organized wire management', parts: 'REV wiring, cable management' },
        { name: 'Final Robot', description: 'Complete assembled robot', parts: 'All systems integrated' }
      ],
      photos: [
        '/assets/IMG_3291.jpeg',
        '/assets/IMG_6006.jpeg',
        '/assets/IMG_8549.jpg',
        '/assets/IMG_8550.jpg'
      ],
      videos: [
        { title: 'Reveal Video', thumbnail: '/assets/IMG_3291.jpeg', url: '#' },
        { title: 'Match Highlights', thumbnail: '/assets/IMG_6006.jpeg', url: '#' },
        { title: 'Build Timelapse', thumbnail: '/assets/IMG_8549.jpg', url: '#' }
      ],
      technical: {
        cad: 'Full CAD model in OnShape with parametric design. Key features include modular subsystems and easy maintenance access.',
        programming: 'Java-based autonomous routines using roadrunner. Implemented vision processing for precise scoring.',
        electrical: 'Dual REV Control Hubs with expansion modules. Clean wire routing with labeled connections.',
        lessons: 'Focus on reliability over complexity. Modular design allowed for quick repairs during competition.'
      }
    },
    '2024-25': {
      name: 'Previous Bot',
      description: 'Our previous season robot with proven competition performance.',
      image: '/assets/IMG_6006.jpeg',
      specs: {
        driveType: 'Tank',
        weight: '18 lbs',
        cycleTime: '10.2s',
        maxSpeed: '2.8 ft/s',
        autoConsistency: '85%',
        keyMechanism: 'Single Intake + Arm',
        controlSystem: 'REV Hub',
        awards: 'Regional Finalist'
      },
      events: [
        { name: 'Regional Championship', placement: '8th', date: '2024-03-20' },
        { name: 'Qualifier #1', placement: '4th', date: '2024-02-05' }
      ],
      buildSteps: [
        { name: 'Chassis', description: 'Standard frame design', parts: 'Aluminum extrusions' },
        { name: 'Drivetrain', description: 'Tank drive system', parts: 'REV HD Hex motors' },
        { name: 'Intake', description: 'Single intake mechanism', parts: 'REV Core Hex motor' },
        { name: 'Lift', description: 'Arm-based lift', parts: 'Servos, arm linkage' },
        { name: 'Outtake', description: 'Simple scoring', parts: 'Servo, scoring mechanism' },
        { name: 'Wiring', description: 'Basic wiring', parts: 'REV wiring' },
        { name: 'Final Robot', description: 'Complete robot', parts: 'All systems' }
      ],
      photos: ['/assets/IMG_6006.jpeg', '/assets/IMG_3291.jpeg'],
      videos: [
        { title: 'Reveal Video', thumbnail: '/assets/IMG_6006.jpeg', url: '#' }
      ],
      technical: {
        cad: 'OnShape CAD with standard design approach.',
        programming: 'Java autonomous with basic routines.',
        electrical: 'Single REV Control Hub setup.',
        lessons: 'Simpler designs are more reliable in competition.'
      }
    }
};

function FTCRobotPage() {
  const [selectedSeason, setSelectedSeason] = useState('2025-26');
  const [buildStep, setBuildStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [visible, setVisible] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const sectionRefs = useRef({});

  const seasons = Object.keys(robotData);

  useEffect(() => {
    const observers = Object.keys(sectionRefs.current).map((key) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.1 }
      );
    });

    Object.keys(sectionRefs.current).forEach((key, index) => {
      if (sectionRefs.current[key]) {
        observers[index].observe(sectionRefs.current[key]);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [selectedSeason]);

  useEffect(() => {
    if (isPlaying && buildStep < robotData[selectedSeason].buildSteps.length - 1) {
      const timer = setTimeout(() => {
        setBuildStep((prev) => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    } else if (buildStep >= robotData[selectedSeason].buildSteps.length - 1) {
      setIsPlaying(false);
    }
  }, [isPlaying, buildStep, selectedSeason]);

  const handleSeasonChange = (season) => {
    setSelectedSeason(season);
    setBuildStep(0);
    setIsPlaying(false);
    setVisible({});
  };

  const currentRobot = robotData[selectedSeason];

  return (
    <div style={{ backgroundColor: 'var(--color-bg-primary)', paddingTop: '100px' }}>
      {/* Hero Section */}
      <section
        ref={(el) => (sectionRefs.current['hero'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.hero ? 1 : 0,
          transform: visible.hero ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-16)',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-4)',
              letterSpacing: '-0.02em'
            }}>
              FTC Robots
            </h1>
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-lg)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.7,
              marginBottom: 'var(--space-8)'
            }}>
              Explore our iterative engineering journey through years of design, competition, and continuous improvement.
            </p>
          </div>
          <div style={{
            aspectRatio: '4/3',
            backgroundImage: `url(${currentRobot.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: '1px solid rgba(0, 16, 17, 0.1)'
            }} />
          </div>
        </div>
      </section>

      {/* Season Selector */}
      <section style={{
        padding: '0 var(--space-4) var(--space-12)',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'sticky',
        top: '80px',
        zIndex: 100,
        backgroundColor: 'var(--color-bg-primary)',
        paddingTop: 'var(--space-4)',
        paddingBottom: 'var(--space-4)'
      }}>
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          borderBottom: '1px solid rgba(0, 16, 17, 0.1)',
          paddingBottom: 'var(--space-4)',
          overflowX: 'auto'
        }}>
          {seasons.map((season) => (
            <button
              key={season}
              onClick={() => handleSeasonChange(season)}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                backgroundColor: selectedSeason === season ? 'var(--color-text-primary)' : 'transparent',
                color: selectedSeason === season ? 'var(--color-text-light)' : 'var(--color-text-primary)',
                border: 'none',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all var(--transition-base)',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                if (selectedSeason !== season) {
                  e.target.style.backgroundColor = 'var(--color-bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedSeason !== season) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {season} {season === '2025-26' && '(Latest)'}
            </button>
          ))}
        </div>
      </section>

      {/* Robot Overview */}
      <section
        ref={(el) => (sectionRefs.current['overview'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.overview ? 1 : 0,
          transform: visible.overview ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-16)',
          marginBottom: 'var(--space-16)'
        }}>
          <div>
            <h2 style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-4)'
            }}>
              {currentRobot.name}
            </h2>
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-base)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.7
            }}>
              {currentRobot.description}
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-4)'
          }}>
            {Object.entries(currentRobot.specs).map(([key, value]) => (
              <div key={key} style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid rgba(0, 16, 17, 0.1)'
              }}>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 'var(--space-1)'
                }}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)'
                }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Build Breakdown */}
      <section
        ref={(el) => (sectionRefs.current['build'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.build ? 1 : 0,
          transform: visible.build ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-12)',
          letterSpacing: '-0.02em'
        }}>
          How It's Built
        </h2>

        {/* Build Progress Scrubber */}
        <div style={{
          marginBottom: 'var(--space-12)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-6)',
            flexWrap: 'wrap',
            gap: 'var(--space-2)'
          }}>
            {currentRobot.buildSteps.map((step, index) => (
              <button
                key={index}
                onClick={() => {
                  setBuildStep(index);
                  setIsPlaying(false);
                }}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  backgroundColor: buildStep >= index ? 'var(--color-text-primary)' : 'transparent',
                  color: buildStep >= index ? 'var(--color-text-light)' : 'var(--color-text-primary)',
                  border: '1px solid rgba(0, 16, 17, 0.1)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-sm)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)',
                  flex: '1',
                  minWidth: '120px',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  if (buildStep < index) {
                    e.target.style.borderColor = 'var(--color-frozen-lake)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (buildStep < index) {
                    e.target.style.borderColor = 'rgba(0, 16, 17, 0.1)';
                  }
                }}
              >
                {step.name}
              </button>
            ))}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-8)'
          }}>
            <button
              onClick={() => {
                setIsPlaying(!isPlaying);
                if (!isPlaying && buildStep >= currentRobot.buildSteps.length - 1) {
                  setBuildStep(0);
                }
              }}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                backgroundColor: 'var(--color-text-primary)',
                color: 'var(--color-text-light)',
                border: 'none',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all var(--transition-base)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--color-frozen-lake)';
                e.target.style.color = 'var(--color-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--color-text-primary)';
                e.target.style.color = 'var(--color-text-light)';
              }}
            >
              {isPlaying ? 'Pause' : 'Replay Build'}
            </button>
            <div style={{
              flex: 1,
              height: '4px',
              backgroundColor: 'var(--color-bg-secondary)',
              position: 'relative'
            }}>
              <div style={{
                width: `${((buildStep + 1) / currentRobot.buildSteps.length) * 100}%`,
                height: '100%',
                backgroundColor: 'var(--color-frozen-lake)',
                transition: 'width 0.5s ease-out'
              }} />
            </div>
          </div>
        </div>

        {/* Build Step Display */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-12)',
          alignItems: 'start'
        }}>
          <div style={{
            aspectRatio: '4/3',
            backgroundColor: 'var(--color-bg-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            border: buildStep === currentRobot.buildSteps.length - 1 ? '2px solid var(--color-frozen-lake)' : '1px solid rgba(0, 16, 17, 0.1)',
            transition: 'all var(--transition-base)'
          }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-2xl)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              textAlign: 'center'
            }}>
              {currentRobot.buildSteps[buildStep].name}
            </div>
            {buildStep === currentRobot.buildSteps.length - 1 && (
              <div style={{
                position: 'absolute',
                top: 'var(--space-4)',
                right: 'var(--space-4)',
                padding: 'var(--space-2) var(--space-4)',
                backgroundColor: 'var(--color-frozen-lake)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                textTransform: 'uppercase'
              }}>
                Complete
              </div>
            )}
          </div>
          <div>
            <h3 style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-2xl)',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-4)'
            }}>
              {currentRobot.buildSteps[buildStep].name}
            </h3>
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-base)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.7,
              marginBottom: 'var(--space-6)'
            }}>
              {currentRobot.buildSteps[buildStep].description}
            </p>
            <div>
              <div style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--space-2)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Parts Used:
              </div>
              <p style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.6
              }}>
                {currentRobot.buildSteps[buildStep].parts}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Performance & Competition Stats */}
      <section
        ref={(el) => (sectionRefs.current['performance'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.performance ? 1 : 0,
          transform: visible.performance ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-12)',
          letterSpacing: '-0.02em'
        }}>
          Performance
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--space-6)'
        }}>
          {currentRobot.events.map((event, index) => (
            <div key={index} style={{
              padding: 'var(--space-6) 0',
              borderBottom: '1px solid rgba(0, 16, 17, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 'var(--space-2)'
              }}>
                <h3 style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xl)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)'
                }}>
                  {event.name}
                </h3>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 700,
                  color: 'var(--color-frozen-lake)'
                }}>
                  {event.placement}
                </div>
              </div>
              <div style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)'
              }}>
                {new Date(event.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Photo + Video Media */}
      <section
        ref={(el) => (sectionRefs.current['media'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.media ? 1 : 0,
          transform: visible.media ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-12)',
          letterSpacing: '-0.02em'
        }}>
          Media Gallery
        </h2>

        {/* Photos Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-16)'
        }}>
          {currentRobot.photos.map((photo, index) => (
            <div
              key={index}
              style={{
                aspectRatio: '4/3',
                backgroundImage: `url(${photo})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                cursor: 'pointer',
                transition: 'transform var(--transition-base)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          ))}
        </div>

        {/* Video Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--space-6)'
        }}>
          {currentRobot.videos.map((video, index) => (
            <div
              key={index}
              onClick={() => setShowVideoModal(video.title)}
              style={{
                position: 'relative',
                aspectRatio: '16/9',
                backgroundImage: `url(${video.thumbnail})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                cursor: 'pointer',
                transition: 'transform var(--transition-base)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 16, 17, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(255, 255, 252, 0.9)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all var(--transition-base)'
                }}>
                  <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-text-primary)', marginLeft: '3px' }}>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
              <div style={{
                position: 'absolute',
                bottom: 'var(--space-4)',
                left: 'var(--space-4)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-base)',
                fontWeight: 600,
                color: 'var(--color-text-light)',
                textShadow: '0 2px 10px rgba(0, 16, 17, 0.5)'
              }}>
                {video.title}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Details Accordion */}
      <section
        ref={(el) => (sectionRefs.current['technical'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.technical ? 1 : 0,
          transform: visible.technical ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-12)',
          letterSpacing: '-0.02em'
        }}>
          Technical Details
        </h2>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)'
        }}>
          {Object.entries(currentRobot.technical).map(([key, value]) => (
            <div key={key} style={{
              borderBottom: '1px solid rgba(0, 16, 17, 0.1)'
            }}>
              <button
                onClick={() => setExpandedAccordion(expandedAccordion === key ? null : key)}
                style={{
                  width: '100%',
                  padding: 'var(--space-6) 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xl)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)'
                }}
              >
                <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{
                    transform: expandedAccordion === key ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform var(--transition-base)'
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div style={{
                maxHeight: expandedAccordion === key ? '500px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease-out',
                paddingBottom: expandedAccordion === key ? 'var(--space-6)' : '0'
              }}>
                <p style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.7
                }}>
                  {value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: 'var(--space-24) var(--space-4)',
        maxWidth: '1400px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'flex',
          gap: 'var(--space-4)',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Link
            to="/join"
            style={{
              padding: '16px 40px',
              backgroundColor: 'var(--color-text-primary)',
              color: 'var(--color-text-light)',
              fontSize: 'var(--text-base)',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              fontFamily: 'var(--font-sans)',
              border: '2px solid var(--color-text-primary)',
              transition: 'all var(--transition-base)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-frozen-lake)';
              e.target.style.borderColor = 'var(--color-frozen-lake)';
              e.target.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-text-primary)';
              e.target.style.borderColor = 'var(--color-text-primary)';
              e.target.style.color = 'var(--color-text-light)';
            }}
          >
            Join FTC
          </Link>
          <Link
            to="/sponsors"
            style={{
              padding: '16px 40px',
              backgroundColor: 'transparent',
              color: 'var(--color-text-primary)',
              fontSize: 'var(--text-base)',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              fontFamily: 'var(--font-sans)',
              border: '2px solid var(--color-text-primary)',
              transition: 'all var(--transition-base)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-text-primary)';
              e.target.style.color = 'var(--color-text-light)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'var(--color-text-primary)';
            }}
          >
            Sponsor Our Builds
          </Link>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div
          onClick={() => setShowVideoModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 16, 17, 0.95)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '1200px',
              width: '100%',
              aspectRatio: '16/9',
              backgroundColor: 'var(--color-text-primary)',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <button
              onClick={() => setShowVideoModal(null)}
              style={{
                position: 'absolute',
                top: 'var(--space-4)',
                right: 'var(--space-4)',
                width: '40px',
                height: '40px',
                backgroundColor: 'rgba(255, 255, 252, 0.9)',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--color-bg-primary)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-sans)'
            }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ marginBottom: 'var(--space-4)' }}>Video Player Placeholder</p>
                <p style={{ fontSize: 'var(--text-sm)' }}>{showVideoModal}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FTCRobotPage;




